<div class="relative block h-full">
  <app-header>
    <div primary-slot>
      <h3 class="font-medium text-xl text-grey-blue">{{ isEdit ? 'Edit Lead' : 'Create Lead' }}</h3>
      <p class="text-xs text-grey-mid font-normal">
        {{ isEdit ? 'Update key fields below' : 'Capture key lead details below' }}
      </p>
    </div>
    <button
      additional-slot
      id="close-modal-button"
      class="btn-primary m-0 flex items-center justify-center w-min p-1"
      (click)="closeModal()"
    >
      <ion-icon class="h-5 w-5" name="close-outline"></ion-icon>
    </button>
  </app-header>

  <div class="flex h-full w-full flex-col space-y-3 gap-3 overflow-y-scroll px-4 pt-3 pb-40 bg-grey-light-concrete">
    <form [formGroup]="leadDetailsForm" class="bg-white rounded shadow p-3 space-y-3">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-4">
        @if (isEdit) {
          <div class="form-input w-full sm:col-span-2">
            <label>Creator</label>
            <app-user-display
              [name]="lead.attributes.creator_name"
              [userId]="lead.attributes.creator_id"
              [date]="lead.attributes.created_at"
              [dateTooltip]="'Toggle Date Format'"
            ></app-user-display>
          </div>
          <div
            class="form-input group relative w-full sm:col-span-2 rounded border-2 border-dashed border-grey-concrete transition-all delay-100"
            [ngClass]="{
              'p-2 pb-6 border-2 bg-white border-grey-concrete lg:border-white hover:shadow-inner hover:bg-grey-light-concrete/30 hover:scale-95 lg:hover:border-grey-concrete':
                !leadCustomerDetailsForm.controls['group_id'].value,
            }"
          >
            <label for="owner-id">Owner</label>
            <div class="font-normal" [class.text-empty-field]="!ownerName">
              {{ ownerName || 'unassigned' }}
            </div>
            <div
              class="absolute bottom-0 right-2 z-10 flex w-full items-end justify-end transition-opacity lg:opacity-0 lg:group-hover:opacity-100"
            >
              <button
                id="change-owner"
                class="my-2 p-0.5 md:p-2 btn-primary flex backdrop-blur-sm shadow-md bg-primary/20 text-turquoise-mid hover:bg-primary relative z-20 hover:text-white transition"
                (click)="presentOwnerSelectorModal()"
                [tooltip]="ownerName ? 'Replace Owner' : 'Select Owner'"
              >
                <ion-icon name="{{ ownerName ? 'swap-horizontal' : 'add' }}" class="size-4"></ion-icon>

                @if (!ownerName) {
                  <span class="block text-xs max-sm:hidden ml-2">Select</span>
                }
              </button>
            </div>
          </div>
        }

        <div
          class="form-input w-full sm:col-span-3"
          [class.has-error]="
            !!(leadDetailsForm.controls['lead_source'].errors && leadDetailsForm.controls['lead_source'].touched)
          "
        >
          @if (!!(leadDetailsForm.controls['lead_source'].errors && leadDetailsForm.controls['lead_source'].touched)) {
            <label for="lead-source-selector">
              <ion-icon name="alert-circle" class="text-base"></ion-icon>
              <div class="error">Source is required</div>
            </label>
          } @else {
            <label class="text-xs font-normal" for="lead-source-selector">Source</label>
          }

          <div class="relative">
            @if (leadDetailsForm.controls['lead_source'].value && !leadDetailsForm.controls['lead_source_tag'].value) {
              <button
                id="add-lead-source-tag"
                type="button"
                class="btn-default btn-xs !absolute !right-7 !top-0.5 !z-0 !m-1.5 inline-flex items-center gap-1"
                tabindex="-1"
                aria-hidden="true"
              >
                <ion-icon name="add-outline" class="size-4"></ion-icon>
                Add tag
              </button>
            }

            <button
              id="lead-source-selector"
              type="button"
              class="btn-default bg-transparent !m-0 !flex min-h-10 w-full cursor-pointer items-center gap-2 !rounded border !p-2 !text-left !text-base !font-normal normal-case not-italic text-grey-blue !ring-2 !ring-white/0 shadow transition focus:!outline-none focus:!ring-2"
              [ngClass]="{
                '!bg-white !bg-none !pr-8 !tracking-normal': leadDetailsForm.controls['lead_source_tag'].value,
                'relative z-10 !bg-none !pr-28': !leadDetailsForm.controls['lead_source_tag'].value,
                '!border-bright-crimson text-bright-crimson focus:!ring-bright-crimson':
                  leadDetailsForm.controls['lead_source'].errors && leadDetailsForm.controls['lead_source'].touched,
                '!border-grey-concrete text-grey-blue focus:!ring-primary':
                  !leadDetailsForm.controls['lead_source'].errors || !leadDetailsForm.controls['lead_source'].touched,
              }"
              aria-label="Change lead source and tag"
              (click)="presentLeadSourceTagPopover($event)"
            >
              <span class="flex max-sm:flex-col min-w-0 flex-1 md:items-center gap-2 overflow-hidden">
                <span
                  class="min-w-0 flex-1 truncate"
                  [ngClass]="{
                    'text-base font-normal text-grey-blue': leadDetailsForm.controls['lead_source'].value,
                    'text-sm font-light text-grey-mid/50': !leadDetailsForm.controls['lead_source'].value,
                    '!text-bright-crimson':
                      leadDetailsForm.controls['lead_source'].errors && leadDetailsForm.controls['lead_source'].touched,
                  }"
                >
                  {{
                    leadDetailsForm.controls['lead_source'].value
                      ? (leadDetailsForm.controls['lead_source'].value | leadSource)
                      : 'Select a lead source...'
                  }}
                </span>
                @if (leadDetailsForm.controls['lead_source_tag'].value) {
                  <span
                    class="inline-flex min-w-0 w-fit md:max-w-[45%] shrink-0 items-center gap-1 overflow-hidden rounded-full bg-primary/15 px-2 py-1 text-xs font-medium leading-4 text-primary"
                  >
                    <ion-icon name="pricetag" class="size-3.5 shrink-0"></ion-icon>
                    <span class="min-w-0 truncate">{{ leadDetailsForm.controls['lead_source_tag'].value }}</span>
                  </span>
                }
              </span>
            </button>

            <ion-icon
              class="pointer-events-none !absolute right-2 top-1/2 size-4 -translate-y-1/2 text-grey-mid"
              name="chevron-down-outline"
            ></ion-icon>
          </div>
        </div>

        <div class="form-input w-full sm:col-span-1">
          <label class="text-xs font-normal" for="quantity">Seat Size</label>
          <input
            id="quantity"
            formControlName="quantity"
            type="text"
            placeholder="optional"
            mask="0*"
            [suffix]="' Seat' | pluralise: leadDetailsForm.controls['quantity'].value : true"
            inputmode="numeric"
          />
        </div>
      </div>

      <div class="form-input w-full">
        <label class="text-xs font-normal" for="description">Description</label>
        <textarea id="description" formControlName="description" rows="3" placeholder="optional"></textarea>
      </div>
    </form>

    <form [formGroup]="leadCustomerDetailsForm" class="rounded bg-white shadow">
      <div class="form-header flex items-center justify-between gap-3 rounded-t">
        <div class="flex items-center gap-2">
          <div
            class="relative flex size-10 items-center justify-center rounded"
            [ngClass]="
              leadCustomerDetailsForm.controls['group_id'].value
                ? leadCustomerDetailsForm.controls['group_entity_type'].value === 'person'
                  ? 'bg-turquoise-bright/15'
                  : 'bg-blue-dark/15'
                : 'border border-grey-concrete bg-white'
            "
          >
            <ion-icon
              [name]="
                leadCustomerDetailsForm.controls['group_entity_type'].value === 'person'
                  ? 'person-outline'
                  : 'business-outline'
              "
              class="size-6"
              [ngClass]="
                leadCustomerDetailsForm.controls['group_id'].value
                  ? leadCustomerDetailsForm.controls['group_entity_type'].value === 'person'
                    ? '!text-turquoise-bright'
                    : '!text-blue-dark'
                  : '!text-grey-mid'
              "
            ></ion-icon>
            @if (leadCustomerDetailsForm.controls['group_id'].value) {
              <span
                class="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full border-2 border-white text-white"
                [ngClass]="
                  leadCustomerDetailsForm.controls['group_entity_type'].value === 'person'
                    ? 'bg-turquoise-bright'
                    : 'bg-blue-dark'
                "
              >
                <ion-icon name="checkmark" class="size-3"></ion-icon>
              </span>
            }
          </div>
          <div>
            <h4>
              {{
                leadCustomerDetailsForm.controls['group_id'].value
                  ? 'Customer'
                  : (leadCustomerDetailsForm.controls['group_entity_type'].value | titlecase)
              }}
            </h4>
            <p
              [ngClass]="{
                '!text-grey-mid': !leadCustomerDetailsForm.controls['group_id'].value,
                '!text-blue-dark':
                  leadCustomerDetailsForm.controls['group_id'].value &&
                  leadCustomerDetailsForm.controls['group_entity_type'].value !== 'person',
                '!text-turquoise-bright':
                  leadCustomerDetailsForm.controls['group_id'].value &&
                  leadCustomerDetailsForm.controls['group_entity_type'].value === 'person',
              }"
            >
              {{
                leadCustomerDetailsForm.controls['group_id'].value
                  ? 'Assigned customer details'
                  : 'No customer assigned'
              }}
            </p>
          </div>
        </div>
        @if (isEdit) {
          @if (leadCustomerDetailsForm.controls['group_id'].value) {
            <button
              id="unlink-customer-group"
              type="button"
              class="btn-default hover:bg-bright-crimson hover:text-white m-0 flex items-center justify-center text-sm"
              (click)="presentUnlinkCustomerConfirmation()"
            >
              <ion-icon name="unlink-outline" class="size-5"></ion-icon>
              <span class="ml-2">Unlink customer</span>
            </button>
          } @else {
            <button
              id="assign-customer-group"
              type="button"
              class="btn-default m-0 flex items-center justify-center text-sm"
              (click)="presentLeadCustomerSelectorModal()"
            >
              <ion-icon name="person-add-outline" class="size-5"></ion-icon>
              <span class="ml-2">Create or link customer</span>
            </button>
          }
        }
      </div>
      <div class="form-body">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="form-input">
            @if (!leadCustomerDetailsForm.controls['group_entity_type'].errors) {
              <label class="text-xs font-normal">Entity Type</label>
            } @else {
              <label id="entity-type-error" class="text-xs font-normal text-bright-crimson">
                Entity Type is required
              </label>
            }
            <div class="flex gap-2">
              <div class="inline-block">
                <input
                  clear-input="true"
                  id="person"
                  name="group_entity_type"
                  class="hidden peer"
                  type="radio"
                  [value]="'person'"
                  formControlName="group_entity_type"
                />
                <label
                  for="person"
                  [ngClass]="{
                    'peer-checked:border-turquoise-mid peer-checked:ring-primary':
                      !leadCustomerDetailsForm.controls['group_entity_type'].errors,
                    'peer-checked:border-bright-crimson peer-checked:ring-bright-crimson text-bright-crimson':
                      !!leadCustomerDetailsForm.controls['group_entity_type'].errors,
                    'border-grey-concrete':
                      leadCustomerDetailsForm.controls['group_entity_type'].value != 'person' &&
                      !leadCustomerDetailsForm.controls['group_entity_type'].errors,
                  }"
                  class="px-3 py-2 text-sm rounded flex items-center border peer-checked:ring-1 shadow hover:cursor-pointer hover:peer-checked:bg-white hover:bg-primary/10 transition-all font-normal text-black"
                >
                  @if (leadCustomerDetailsForm.controls['group_entity_type'].value != 'person') {
                    <ion-icon class="text-grey-concrete mr-2 text-lg" name="ellipse-outline"></ion-icon>
                  }
                  @if (leadCustomerDetailsForm.controls['group_entity_type'].value == 'person') {
                    <ion-icon
                      class="mr-2 text-lg"
                      [ngClass]="
                        !!leadCustomerDetailsForm.controls['group_entity_type'].errors
                          ? 'text-bright-crimson'
                          : 'text-turquoise-mid'
                      "
                      name="checkmark-circle"
                    ></ion-icon>
                  }
                  Person
                </label>
              </div>

              <div class="inline-block">
                <input
                  clear-input="true"
                  id="company"
                  name="group_entity_type"
                  class="hidden peer"
                  type="radio"
                  [value]="'company'"
                  formControlName="group_entity_type"
                />
                <label
                  for="company"
                  [ngClass]="{
                    'border-turquoise-mid': leadCustomerDetailsForm.controls['group_entity_type'].value == 'company',
                  }"
                  class="px-3 py-2 text-sm rounded flex items-center border border-grey-concrete hover:cursor-pointer peer-checked:border-turquoise-mid peer-checked:ring-1 peer-checked:ring-primary hover:bg-primary/10 hover:peer-checked:bg-white transition-all font-normal text-black"
                >
                  @if (leadCustomerDetailsForm.controls['group_entity_type'].value != 'company') {
                    <ion-icon class="text-grey-concrete mr-2 text-lg" name="ellipse-outline"></ion-icon>
                  }
                  @if (leadCustomerDetailsForm.controls['group_entity_type'].value == 'company') {
                    <ion-icon class="text-turquoise-mid mr-2 text-lg" name="checkmark-circle"></ion-icon>
                  }
                  Company
                </label>
              </div>
            </div>
          </div>

          <div
            class="form-input group relative w-full rounded border-grey-concrete transition-all delay-100"
            [ngClass]="{
              'p-2 pb-6 bg-white border-grey-concrete hover:shadow-inner hover:bg-grey-light-concrete/30 hover:scale-95':
                !leadCustomerDetailsForm.controls['group_id'].value,
              'border-2 border-dashed lg:border-white lg:hover:border-grey-concrete':
                leadCustomerDetailsForm.controls['parent_group_id'].value,
              'border-2 border-dashed scale-95': !leadCustomerDetailsForm.controls['parent_group_id'].value,
            }"
          >
            <label for="parent-group-id">{{
              leadCustomerDetailsForm.controls['group_id'].value ? 'Parent' : 'Intended Parent'
            }}</label>
            <div class="min-w-0">
              @if (leadCustomerDetailsForm.controls['parent_group_id'].value) {
                <app-group-display
                  [groupId]="leadCustomerDetailsForm.controls['parent_group_id'].value"
                  [groupName]="leadCustomerDetailsForm.controls['parent_group_name'].value"
                  [enableLink]="false"
                ></app-group-display>
              } @else {
                <span class="font-medium text-empty-field">unassigned</span>
              }
            </div>
            @if (!leadCustomerDetailsForm.controls['group_id'].value) {
              <div
                class="absolute bottom-0 right-2 z-10 flex w-full items-end justify-end transition-opacity"
                [ngClass]="{
                  'lg:opacity-0 lg:group-hover:opacity-100': leadCustomerDetailsForm.controls['parent_group_id'].value,
                }"
              >
                <button
                  id="change-parent"
                  type="button"
                  class="my-2 p-0.5 md:p-2 btn-primary flex backdrop-blur-sm shadow-md bg-primary/20 text-turquoise-mid hover:bg-primary !hover:text-white relative z-20 hover:text-white transition"
                  (click)="parentSelector.presentPopover($event)"
                  [tooltip]="
                    leadCustomerDetailsForm.controls['parent_group_id'].value ? 'Change Parent' : 'Select Parent'
                  "
                >
                  <ion-icon
                    [name]="leadCustomerDetailsForm.controls['parent_group_id'].value ? 'swap-horizontal' : 'add'"
                    class="size-4"
                  ></ion-icon>
                  @if (!leadCustomerDetailsForm.controls['parent_group_id'].value) {
                    <span class="ml-2 block text-xs">Select</span>
                  }
                </button>
              </div>
            }
          </div>
          <app-group-selector-container
            #parentSelector
            title="Select Parent"
            [groupFilters]="{ group_type: ['partner', 'distributor'] }"
            [showRemoveParent]="leadCustomerDetailsForm.controls['parent_group_id'].value"
            (selectedGroup)="selectParent($any($event))"
            presentation="popover"
          ></app-group-selector-container>
        </div>

        <div
          class="form-input w-full"
          [ngClass]="{
            'has-error':
              leadCustomerDetailsForm.controls['customer_name'].errors &&
              leadCustomerDetailsForm.controls['customer_name'].touched,
          }"
        >
          @if (
            !(
              leadCustomerDetailsForm.controls['customer_name'].errors &&
              leadCustomerDetailsForm.controls['customer_name'].touched
            )
          ) {
            <label class="text-xs font-normal flex items-center"
              >{{
                leadCustomerDetailsForm.controls['group_entity_type'].value == 'company'
                  ? 'Company Name'
                  : 'Customer Name'
              }}
            </label>
          }
          @if (
            leadCustomerDetailsForm.controls['customer_name'].errors &&
            leadCustomerDetailsForm.controls['customer_name'].touched
          ) {
            <label>
              <ion-icon name="alert-circle" class="text-base"></ion-icon>
              @if (leadCustomerDetailsForm.controls['customer_name'].errors?.['required']) {
                <div id="required-name" class="error">
                  {{
                    leadCustomerDetailsForm.controls['group_entity_type'].value == 'company' ? 'Company' : 'Customer'
                  }}
                  name is required
                </div>
              }
              @if (leadCustomerDetailsForm.controls['customer_name'].hasError('maxlength')) {
                <div class="error">
                  {{
                    leadCustomerDetailsForm.controls['group_entity_type'].value == 'company' ? 'Company' : 'Customer'
                  }}
                  name is too long
                </div>
              }
              @if (leadCustomerDetailsForm.controls['customer_name'].hasError('pattern')) {
                <div class="error">
                  {{
                    leadCustomerDetailsForm.controls['group_entity_type'].value == 'company' ? 'Company' : 'Customer'
                  }}
                  name cannot begin with "= + - / * &#64; | &lt; &gt;" characters
                </div>
              }
            </label>
          }
          <input
            id="customer-name"
            autofocus
            clear-input="true"
            formControlName="customer_name"
            max="100"
            [ngClass]="{
              'border-bright-crimson text-bright-crimson focus:ring-1 focus:ring-bright-crimson':
                leadCustomerDetailsForm.controls['customer_name'].errors &&
                leadCustomerDetailsForm.controls['customer_name'].touched,
            }"
          />
        </div>
        @if (leadCustomerDetailsForm.controls['group_entity_type'].value == 'company') {
          <div class="form-input w-full">
            <label class="text-xs font-normal flex items-center" for="contact-name">Contact Name</label>
            <input
              id="contact-name"
              clear-input="true"
              formControlName="contact_name"
              placeholder="optional"
              type="text"
            />
          </div>
        }

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div
            class="form-input w-full sm:col-span-3"
            [ngClass]="{
              'has-error':
                leadCustomerDetailsForm.controls['contact_email'].errors &&
                leadCustomerDetailsForm.controls['contact_email'].touched,
            }"
          >
            @if (
              !(
                leadCustomerDetailsForm.controls['contact_email'].errors &&
                leadCustomerDetailsForm.controls['contact_email'].touched
              )
            ) {
              <label class="text-xs font-normal flex items-center" for="contact-email"
                >{{
                  leadCustomerDetailsForm.controls['group_entity_type'].value == 'company'
                    ? 'Company Email'
                    : 'Customer Email'
                }}
              </label>
            }
            @if (
              leadCustomerDetailsForm.controls['contact_email'].errors &&
              leadCustomerDetailsForm.controls['contact_email'].touched &&
              leadCustomerDetailsForm.controls['contact_email'].hasError('required')
            ) {
              <label
                id="required-email"
                for="contact-email"
                class="text-xs font-normal flex items-center text-bright-crimson"
              >
                <ion-icon name="alert-circle" class="size-4 m-0 mx-1"></ion-icon>
                {{
                  leadCustomerDetailsForm.controls['group_entity_type'].value == 'company'
                    ? 'Company Email'
                    : 'Customer Email'
                }}
                is required
              </label>
            }
            @if (
              leadCustomerDetailsForm.controls['contact_email'].hasError('email') &&
              leadCustomerDetailsForm.controls['contact_email'].touched
            ) {
              <label
                id="invalid-email"
                for="contact-email"
                class="text-xs font-normal flex items-center text-bright-crimson"
              >
                <ion-icon name="alert-circle" class="size-4 m-0 mx-1"></ion-icon>
                {{
                  leadCustomerDetailsForm.controls['group_entity_type'].value == 'company'
                    ? 'Company Email'
                    : 'Customer Email'
                }}
                is invalid
              </label>
            }
            <input
              id="contact-email"
              clear-input="true"
              formControlName="contact_email"
              type="email"
              [ngClass]="{
                'border-bright-crimson text-bright-crimson focus:ring-1 focus:ring-bright-crimson':
                  leadCustomerDetailsForm.controls['contact_email'].errors &&
                  leadCustomerDetailsForm.controls['contact_email'].touched,
              }"
            />
          </div>

          <div
            class="form-input w-full sm:col-span-1"
            [ngClass]="{
              'has-error':
                leadCustomerDetailsForm.controls['vat_number'].errors &&
                leadCustomerDetailsForm.controls['vat_number'].touched,
            }"
          >
            @if (
              !(
                leadCustomerDetailsForm.controls['vat_number'].errors &&
                leadCustomerDetailsForm.controls['vat_number'].touched
              )
            ) {
              <label class="text-xs font-normal">VAT Number</label>
            } @else {
              <label>
                <ion-icon name="alert-circle" class="text-base"></ion-icon>
                @if (leadCustomerDetailsForm.controls['vat_number'].hasError('minlength')) {
                  <div class="error">VAT Number too short</div>
                }
                @if (leadCustomerDetailsForm.controls['vat_number'].hasError('maxlength')) {
                  <div class="error">VAT Number too long</div>
                }
              </label>
            }
            <input
              id="vat-number"
              placeholder="optional"
              clear-input="true"
              formControlName="vat_number"
              type="text"
              [mask]="leadCustomerDetailsForm.controls['country_alpha2_code'].value === 'ZA' ? '0*' : ''"
              [ngClass]="{
                'border-bright-crimson text-bright-crimson focus:ring-1 focus:ring-bright-crimson':
                  leadCustomerDetailsForm.controls['vat_number'].errors &&
                  leadCustomerDetailsForm.controls['vat_number'].touched,
              }"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div
            class="form-input w-full"
            [ngClass]="{
              'has-error':
                leadCustomerDetailsForm.controls['country_alpha2_code'].errors &&
                leadCustomerDetailsForm.controls['country_alpha2_code'].touched,
            }"
          >
            @if (
              !(
                leadCustomerDetailsForm.controls['country_alpha2_code'].errors &&
                leadCustomerDetailsForm.controls['country_alpha2_code'].touched
              )
            ) {
              <label class="text-xs font-normal">Country</label>
            } @else {
              <label>
                <ion-icon name="alert-circle" class="text-base"></ion-icon>
                @if (leadCustomerDetailsForm.controls['country_alpha2_code'].hasError('required')) {
                  <div class="error">Country is required</div>
                }
              </label>
            }
            <select
              id="country"
              formControlName="country_alpha2_code"
              class="form-control"
              [ngClass]="{
                'border-bright-crimson text-bright-crimson focus:ring-1 focus:ring-bright-crimson':
                  leadCustomerDetailsForm.controls['country_alpha2_code'].errors &&
                  leadCustomerDetailsForm.controls['country_alpha2_code'].touched,
              }"
            >
              @for (country of countries$ | async; track country?.attributes?.alpha2_code ?? $index) {
                <option [value]="country?.attributes?.alpha2_code">
                  {{ country?.attributes?.alpha2_code | emojiFlag }} &nbsp;{{ country?.attributes?.name }}
                </option>
              }
            </select>
          </div>

          <div class="form-input w-full">
            <label class="text-xs font-normal flex items-center">Telephone Number</label>
            <div class="flex rounded-md shadow-sm">
              @if (selectedCountry$ | async) {
                <span class="form-prefix">
                  <span class="text-xl">{{ (selectedCountry$ | async).attributes.alpha2_code | emojiFlag }}</span>
                </span>
              }
              <input
                id="contact-telephone-number"
                formControlName="contact_telephone_number"
                placeholder="optional"
                type="tel"
                class="rounded-none rounded-r-md"
                [ngClass]="{ 'border-l-0': !!(selectedCountry$ | async) }"
                mask="0000009999"
                [prefix]="(selectedCountry$ | async) ? '+' + (selectedCountry$ | async).attributes.dial_code + ' ' : ''"
              />
            </div>
          </div>
        </div>

        @if (leadCustomerDetailsForm.controls['group_entity_type'].value == 'company') {
          <div
            class="form-input w-full"
            [ngClass]="{
              'has-error':
                leadCustomerDetailsForm.controls['company_registration_number'].errors &&
                leadCustomerDetailsForm.controls['company_registration_number'].touched,
            }"
          >
            @if (
              !(
                leadCustomerDetailsForm.controls['company_registration_number'].errors &&
                leadCustomerDetailsForm.controls['company_registration_number'].touched
              )
            ) {
              <label class="text-xs font-normal flex items-center">Registration Number</label>
            }
            @if (
              leadCustomerDetailsForm.controls['company_registration_number'].errors &&
              leadCustomerDetailsForm.controls['company_registration_number'].touched
            ) {
              <label>
                <ion-icon name="alert-circle" class="text-base"></ion-icon>
                @if (leadCustomerDetailsForm.controls['company_registration_number'].hasError('maxlength')) {
                  <div id="max" class="error">Registration number is too long</div>
                }
                @if (leadCustomerDetailsForm.controls['company_registration_number'].hasError('minlength')) {
                  <div id="min" class="error">Registration number is too short</div>
                }
                @if (leadCustomerDetailsForm.controls['company_registration_number'].hasError('pattern')) {
                  <div id="invalid" class="error">Registration number is invalid</div>
                }
              </label>
            }
            <input
              type="text"
              id="company-registration-number"
              formControlName="company_registration_number"
              placeholder="optional"
              [maxLength]="100"
              [minLength]="6"
              class="peer"
            />
            <div
              id="crn-hint"
              class="text-grey-blue/50 pt-2 pl-0.5 text-xxs -mt-0.5 font-normal peer-focus:text-black transition-all"
            >
              Company Registration Number, VAT number or other official entity identifier.
            </div>
          </div>
        }

        @if (isEdit && leadCustomerDetailsForm.controls['group_id'].value) {
          <div
            id="full-customer-edit"
            class="rounded bg-grey-light-concrete hover:bg-grey-concrete cursor-pointer mt-2 p-2 pr-0 flex w-full text-grey-blue border border-grey-concrete"
            (click)="navigateToFullEditCustomerDetails()"
          >
            <div>
              <h2 class="text-xs font-medium">Edit more details</h2>
              <p class="text-xs">Click to navigate to the full editing screen.</p>
            </div>
            <ion-icon name="chevron-forward-outline" class="text-grey-mid size-6 my-auto ml-auto"></ion-icon>
          </div>
        }
      </div>
    </form>
  </div>

  <div class="flex flex-row gap-2 p-3 fixed bottom-0 w-full border-t-2 border-grey-concrete backdrop-blur-sm">
    @if (leadDetailsForm.dirty || leadCustomerDetailsForm.dirty) {
      <button id="save-lead-btn" class="btn-primary text-sm" (click)="saveLead()">
        {{ isEdit ? 'Save Changes' : 'Create Lead' }}
      </button>
    }
    <button class="btn-default text-sm" (click)="closeModal()">Cancel</button>
  </div>
</div>
